import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

type LayoutType = 'modern' | 'classic';

interface LayoutContextType {
  layoutType: LayoutType;
  setLayoutType: (type: LayoutType) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

// ── Sidebar Width Context ───────────────────────────────────────────────
// Allows layout components to communicate sidebar width to the player.
// The player is position:fixed so it needs an explicit left offset.

const DEFAULT_SIDEBAR_WIDTH = 0;

interface SidebarWidthContextType {
  leftSidebarWidth: number;
  setLeftSidebarWidth: (width: number) => void;
}

const SidebarWidthContext = createContext<SidebarWidthContextType>({
  leftSidebarWidth: DEFAULT_SIDEBAR_WIDTH,
  setLeftSidebarWidth: () => {},
});

export const useSidebarWidth = () => useContext(SidebarWidthContext);

// Storage key for scroll position
const SCROLL_POSITION_KEY = 'luma-scroll-before-layout-change';

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

interface LayoutProviderProps {
  children: React.ReactNode;
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const [layoutType, setLayoutType] = useState<LayoutType>(() => {
    const saved = localStorage.getItem('luma-layout-preference');
    return (saved as LayoutType) || 'classic';
  });

  const isTransitioningRef = useRef(false);
  const prevLayoutRef = useRef<LayoutType>(layoutType);

  useEffect(() => {
    // Check if layout actually changed
    if (prevLayoutRef.current !== layoutType) {
      isTransitioningRef.current = true;
      prevLayoutRef.current = layoutType;
    }

    localStorage.setItem('luma-layout-preference', layoutType);
    
    // Restore scroll position after layout change
    if (isTransitioningRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const savedScroll = localStorage.getItem(SCROLL_POSITION_KEY);
          if (savedScroll) {
            try {
              const { scrollY } = JSON.parse(savedScroll);
              window.scrollTo(0, scrollY);
              localStorage.removeItem(SCROLL_POSITION_KEY);
            } catch (e) {
              console.error('Failed to restore scroll position:', e);
            }
          }
          isTransitioningRef.current = false;
        });
      });
    }
  }, [layoutType]);

  const value = {
    layoutType,
    setLayoutType,
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export default LayoutProvider;
